import type { PolicyStatePremiumViewModel } from '@/api/types/PolicyStatePremiumViewModel';

import dayjs from 'dayjs';
import { useParams } from 'react-router';
import { useBoolean } from 'minimal-shared/hooks';
import { memo, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InputAdornment from '@mui/material/InputAdornment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Card,
  Table,
  Tooltip,
  MenuItem,
  Collapse,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  IconButton,
  Typography,
  CardContent,
  TableContainer,
} from '@mui/material';

import { usePolicyPeriodsByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';

import { StyledTooltipLabel } from 'src/sections/employer-data/shared/styles/sharedStyles';
import { formatDate, formatDollarAmount } from 'src/sections/employer-data/shared/utils/policyUtils';

import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import {
  buildPolicyPeriodPayload,
  normalizeRatingInput,
  validatePolicyPeriodDraft,
  initializePolicyPeriodDraft,
  hasPolicyPeriodDraftChanges,
  getPolicyPeriodsDraftDelta,
  applyPolicyPeriodsDraftDelta,
  buildPolicyPeriodChangedFieldSummaries,
} from './policy-periods-edit.types';

import type { PolicyPeriodEditDraft, PolicyPeriodFieldErrors } from './policy-periods-edit.types';
import type { PolicyPeriodsProps, PolicyPeriodsHandle } from './policy-periods.types';

type PolicyPeriodRow = {
  policyPeriodId: number;
  effectiveDate?: string;
  governingClass?: string | null;
  governingClassDescription?: string | null;
  expRating?: string | null;
  estStandardPremiumTotal?: number;
  policyStatePremiums: PolicyStatePremiumViewModel[];
};

type EditableDatePickerProps = {
  testId: string;
  label: string;
  value: string;
  error?: string;
  onValidChange: (value: dayjs.Dayjs | null) => void;
};

function EditableDatePicker({ testId, label, value, error, onValidChange }: Readonly<EditableDatePickerProps>) {
  const [pickerValue, setPickerValue] = useState(() => (value ? dayjs(value, 'MM/DD/YYYY') : null));
  const [hasInputError, setHasInputError] = useState(false);

  useEffect(() => {
    if (!hasInputError) {
      setPickerValue(value ? dayjs(value, 'MM/DD/YYYY') : null);
    }
  }, [hasInputError, value]);

  return (
    <div data-testid={testId}>
      <DatePicker
        label={label}
        format="MM/DD/YYYY"
        value={pickerValue}
        onChange={(nextValue) => {
          setPickerValue(nextValue);
          if (nextValue === null || nextValue.isValid()) {
            setHasInputError(false);
            onValidChange(nextValue);
          }
        }}
        onError={(reason) => {
          setHasInputError(Boolean(reason));
          if (reason) onValidChange(null);
        }}
        slotProps={{ textField: { fullWidth: true, size: 'small', error: Boolean(error), helperText: error } }}
      />
    </div>
  );
}

function PolicyPeriodRowView({
  row,
  isEditMode,
  draft,
  setDraft,
  fieldErrors,
  experienceRatingLookups,
}: Readonly<{
  row: PolicyPeriodRow;
  isEditMode: boolean;
  draft?: PolicyPeriodEditDraft;
  setDraft: React.Dispatch<React.SetStateAction<PolicyPeriodEditDraft>>;
  fieldErrors: PolicyPeriodFieldErrors;
  experienceRatingLookups: PolicyPeriodsProps['experienceRatingLookups'];
}>) {
  const open = useBoolean();

  const updateStatePremium = (
    statePremiumId: number,
    patch: Partial<PolicyPeriodEditDraft['statePremiums'][number]>
  ) => {
    setDraft((current) => ({
      ...current,
      statePremiums: current.statePremiums.map((item) =>
        item.statePremiumId === statePremiumId ? { ...item, ...patch } : item
      ),
    }));
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={open.onToggle}>
            {open.value ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell data-testid="periodEffectiveDate">
          {isEditMode && draft ? (
            <EditableDatePicker
              testId="policy-period-update-period-effective-date"
              label="Period effective date"
              value={draft.effectiveDate}
              error={fieldErrors.effectiveDate}
              onValidChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  effectiveDate: value?.isValid() ? value.format('MM/DD/YYYY') : '',
                }))
              }
            />
          ) : (
            formatDate(row.effectiveDate)
          )}
        </TableCell>
        <TableCell data-testid="governingClass">
          {row.governingClass && row.governingClassDescription ? (
            <Tooltip arrow title={row.governingClassDescription} placement="top">
              <StyledTooltipLabel>
                <Typography component="span" sx={{ typography: 'body2' }}>
                  {row.governingClass}
                </Typography>
              </StyledTooltipLabel>
            </Tooltip>
          ) : (
            row.governingClass ?? ''
          )}
        </TableCell>
        <TableCell data-testid="experienceRating">
          {isEditMode && draft ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Experience rating"
              value={
                (experienceRatingLookups ?? []).some(
                  (option) => option.experienceRatingCode === draft.experienceRatingCode
                )
                  ? draft.experienceRatingCode
                  : ''
              }
              onChange={(event) =>
                setDraft((current) => ({ ...current, experienceRatingCode: event.target.value }))
              }
              error={Boolean(fieldErrors.experienceRatingCode)}
              helperText={fieldErrors.experienceRatingCode}
              data-testid="policy-period-update-experience-rating"
            >
              {(experienceRatingLookups ?? []).map((option) => (
                <MenuItem key={option.experienceRatingCode} value={option.experienceRatingCode}>
                  {option.experienceRatingDescription}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            row.expRating
          )}
        </TableCell>
        <TableCell data-testid="premiumTotal">
          {isEditMode && draft ? (
            <TextField
              fullWidth
              size="small"
              label="Est. standard premium total"
              value={draft.estimatedStandardPremium}
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                htmlInput: { maxLength: 10, inputMode: 'numeric' },
              }}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  estimatedStandardPremium: event.target.value.replace(/\D/g, ''),
                }))
              }
              error={Boolean(fieldErrors.estimatedStandardPremium)}
              helperText={fieldErrors.estimatedStandardPremium}
              data-testid="policy-period-update-estimated-standard-premium"
            />
          ) : (
            formatDollarAmount(row.estStandardPremiumTotal)
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open.value} timeout="auto" unmountOnExit>
            <Box
              sx={{ pb: 2, pt: 2, pl: isEditMode ? 11 : 0 }}
              data-testid={`policy-period-row-${row.policyPeriodId}`}
            >
              <TableContainer sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
                <Table aria-label="rating-period-details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rating period</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Premium</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.policyStatePremiums.map((policyStatePremium) => {
                      const statePremiumId = policyStatePremium.statePremiumId ?? 0;
                      const rowIndex = draft?.statePremiums.findIndex(
                        (item) => item.statePremiumId === statePremiumId
                      ) ?? -1;
                      const draftRow = rowIndex >= 0 ? draft?.statePremiums[rowIndex] : undefined;
                      const rowErrors = rowIndex >= 0 ? (fieldErrors.statePremiums?.[rowIndex] ?? {}) : {};
                      return (
                        <TableRow key={statePremiumId}>
                          <TableCell
                            sx={{ borderBottom: '1px dashed var(--palette-divider) !important' }}
                            data-testid={`ratingPeriod-${statePremiumId}`}
                          >
                            {isEditMode && draftRow ? (
                              <EditableDatePicker
                                testId={`policy-period-update-rating-period-${statePremiumId}`}
                                label="Rating period"
                                value={draftRow.policyChangeEffectiveDate}
                                error={rowErrors.policyChangeEffectiveDate}
                                onValidChange={(value) =>
                                  updateStatePremium(statePremiumId, {
                                    policyChangeEffectiveDate: value?.isValid() ? value.format('MM/DD/YYYY') : '',
                                  })
                                }
                              />
                            ) : (
                              formatDate(policyStatePremium.policyChangeEffectiveDate)
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: '1px dashed var(--palette-divider) !important' }}
                            data-testid={`rating-${statePremiumId}`}
                          >
                            {isEditMode && draftRow ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={draftRow.rating}
                                onChange={(event) =>
                                  updateStatePremium(statePremiumId, { rating: normalizeRatingInput(event.target.value) })
                                }
                                slotProps={{ htmlInput: { maxLength: 4, inputMode: 'decimal' } }}
                                data-testid={`policy-period-update-rating-${statePremiumId}`}
                                error={Boolean(rowErrors.rating)}
                                helperText={rowErrors.rating}
                              />
                            ) : (
                              policyStatePremium.expModFactor?.toFixed(2)
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: '1px dashed var(--palette-divider) !important' }}
                            data-testid={`premium-${statePremiumId}`}
                          >
                            {isEditMode && draftRow ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={draftRow.premium}
                                slotProps={{
                                  input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                                  htmlInput: { maxLength: 10, inputMode: 'numeric' },
                                }}
                                onChange={(event) =>
                                  updateStatePremium(statePremiumId, { premium: event.target.value.replace(/\D/g, '') })
                                }
                                data-testid={`policy-period-update-premium-${statePremiumId}`}
                                error={Boolean(rowErrors.premium)}
                                helperText={rowErrors.premium}
                              />
                            ) : (
                              formatDollarAmount(policyStatePremium.estimatedStateStandardPremiumTotal)
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function PolicyPeriodsTable({
  dataTestId,
  title,
  rows,
  columns,
  isEditMode,
  drafts,
  setDraftForPeriod,
  fieldErrorsByPeriod,
  experienceRatingLookups,
}: Readonly<{
  dataTestId: string;
  title: string;
  rows: PolicyPeriodRow[];
  columns: string[];
  isEditMode: boolean;
  drafts: Record<number, PolicyPeriodEditDraft>;
  setDraftForPeriod: (
    policyPeriodId: number,
    updater: (current: PolicyPeriodEditDraft) => PolicyPeriodEditDraft
  ) => void;
  fieldErrorsByPeriod: Record<number, PolicyPeriodFieldErrors>;
  experienceRatingLookups: PolicyPeriodsProps['experienceRatingLookups'];
}>) {
  return (
    <Box>
      <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="policy-periods-title">
        {title}
      </Typography>
      <TableContainer sx={{ mt: 3, borderBottom: '1px dashed var(--palette-divider)' }}>
        <Table data-testid={dataTestId}>
          <TableHead>
            <TableRow>
              <TableCell />
              {columns.map((column) => (
                <TableCell key={column} sx={{ width: `${100 / columns.length}%` }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <PolicyPeriodRowView
                key={row.policyPeriodId}
                row={row}
                isEditMode={isEditMode}
                draft={drafts[row.policyPeriodId]}
                setDraft={(value) =>
                  setDraftForPeriod(row.policyPeriodId, (current) =>
                    typeof value === 'function'
                      ? (value as (currentValue: PolicyPeriodEditDraft) => PolicyPeriodEditDraft)(current)
                      : value
                  )
                }
                fieldErrors={fieldErrorsByPeriod[row.policyPeriodId] ?? {}}
                experienceRatingLookups={experienceRatingLookups}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const PolicyPeriods = forwardRef<PolicyPeriodsHandle, PolicyPeriodsProps>(function PolicyPeriodsTab(
  { comboId = 0, coverageId = 0, policyId = 0, isEditMode = false, experienceRatingLookups = [], initialDraftDelta = null, onDraftDeltaChange },
  ref
) {
  const [drafts, setDrafts] = useState<Record<number, PolicyPeriodEditDraft>>({});
  const [snapshots, setSnapshots] = useState<Record<number, PolicyPeriodEditDraft>>({});
  const [fieldErrorsByPeriod, setFieldErrorsByPeriod] = useState<Record<number, PolicyPeriodFieldErrors>>({});
  const initialDraftDeltaRef = useRef(initialDraftDelta);
  const hasAppliedInitialDeltaRef = useRef(false);
  // Tracks the last viewPeriods reference actually processed, so a mount-effect re-invocation with
  // unchanged data (e.g. Strict Mode's double-invoke) is a no-op instead of re-resetting drafts.
  const lastProcessedViewPeriodsRef = useRef<unknown>(undefined);

  const validateForSave = (): boolean => {
    const nextErrors: Record<number, PolicyPeriodFieldErrors> = {};
    Object.values(drafts).forEach((draft) => {
      nextErrors[draft.policyPeriodId] = validatePolicyPeriodDraft(draft);
    });
    setFieldErrorsByPeriod(nextErrors);
    return Object.values(nextErrors).every((errors) => Object.keys(errors).length === 0);
  };

  useImperativeHandle(ref, () => ({
    validateForSave,
    hasPendingChanges: () =>
      Object.values(drafts).some((draft) => {
        const snapshot = snapshots[draft.policyPeriodId];
        return snapshot ? hasPolicyPeriodDraftChanges(snapshot, draft) : false;
      }),
    getChangedFieldSummaries: () =>
      Object.values(drafts).flatMap((draft) => {
        const snapshot = snapshots[draft.policyPeriodId];
        if (!snapshot) {
          return [];
        }

        const summaries = buildPolicyPeriodChangedFieldSummaries(snapshot, draft, experienceRatingLookups);
        return summaries.map((summary) => `Period ${snapshot.effectiveDate}: ${summary}`);
      }),
    getPolicyPeriodsPayload: () =>
      Object.values(drafts)
        .map((draft) => {
          const snapshot = snapshots[draft.policyPeriodId];
          return snapshot ? buildPolicyPeriodPayload(snapshot, draft) : null;
        })
        .filter((payload): payload is NonNullable<typeof payload> => payload !== null),
    getPrimaryPolicyPeriodId: () => viewPeriods?.[0]?.policyPeriodId ?? 0,
    commitEdit: () => setSnapshots(drafts),
    cancelEdit: () => setDrafts(snapshots),
  }));

  const routeParams = useParams<{ comboId: string; coverageId: string; policyId: string }>();
  const { data } = usePolicyPeriodsByPolicyId(
    comboId || Number(routeParams.comboId),
    coverageId || Number(routeParams.coverageId),
    policyId || Number(routeParams.policyId)
  );
  const viewPeriods = data?.data;

  useEffect(() => {
    if (!viewPeriods || lastProcessedViewPeriodsRef.current === viewPeriods) {
      return;
    }
    lastProcessedViewPeriodsRef.current = viewPeriods;

    const nextDrafts: Record<number, PolicyPeriodEditDraft> = {};
    viewPeriods.forEach((period) => {
      const policyPeriodId = period.policyPeriodId ?? 0;
      nextDrafts[policyPeriodId] = initializePolicyPeriodDraft(period);
    });
    setSnapshots(nextDrafts);
    if (!hasAppliedInitialDeltaRef.current) {
      hasAppliedInitialDeltaRef.current = true;
      setDrafts(applyPolicyPeriodsDraftDelta(nextDrafts, initialDraftDeltaRef.current));
    } else {
      setDrafts(nextDrafts);
    }
    setFieldErrorsByPeriod({});
  }, [viewPeriods]);

  useEffect(() => {
    if (!hasAppliedInitialDeltaRef.current || !viewPeriods) {
      return;
    }
    onDraftDeltaChange?.(getPolicyPeriodsDraftDelta(snapshots, drafts));
  }, [drafts, snapshots, onDraftDeltaChange, viewPeriods]);

  const setDraftForPeriod = (
    policyPeriodId: number,
    updater: (current: PolicyPeriodEditDraft) => PolicyPeriodEditDraft
  ) => {
    setDrafts((current) => {
      const currentDraft = current[policyPeriodId];
      if (!currentDraft) {
        return current;
      }

      return { ...current, [policyPeriodId]: updater(currentDraft) };
    });
  };

  const headerWidth = useHeaderWidth();

  return (
    <Card
      sx={{
        display: 'flex',
        zIndex: 'unset',
        overflow: 'unset',
        mt: 5,
        borderRadius: 2,
        boxShadow: 'var(--customShadows-card)',
        width: headerWidth,
      }}
    >
      <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="policy-periods-card">
        <Box sx={{ pt: 1.5, px: 1.5 }}>
          <PolicyPeriodsTable
            dataTestId="policy-periods-table"
            title="Policy periods"
            columns={[
              'Period effective date',
              'Governing class code',
              'Experience rating',
              'Est. standard premium total',
            ]}
            rows={
              viewPeriods?.map((period) => ({
                policyPeriodId: period.policyPeriodId ?? 0,
                effectiveDate: period.effectiveDate,
                governingClass: period.governingClass?.classCode,
                governingClassDescription: period.governingClass?.description,
                expRating: period.experienceRating,
                estStandardPremiumTotal: period.estimatedStandardPremiumTotal,
                policyStatePremiums: period.policyStatePremiums ?? [],
              })) ?? []
            }
            isEditMode={isEditMode}
            drafts={drafts}
            setDraftForPeriod={setDraftForPeriod}
            fieldErrorsByPeriod={fieldErrorsByPeriod}
            experienceRatingLookups={experienceRatingLookups}
          />
        </Box>
      </CardContent>
    </Card>
  );
});

export default memo(PolicyPeriods);

